import { toaster } from '@/components/ui/toaster';
import { discoveryPreferencesQueryKey } from '@/hooks/queries/use-discovery-preferences-query';
import api from '@/libs/axiosInstance';
import type { BrandDiscoveryPreferences } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

const putDiscoveryPreferences = async (
  prefs: BrandDiscoveryPreferences
): Promise<BrandDiscoveryPreferences> => {
  const { data } = await api.put<{ data: { preferences: BrandDiscoveryPreferences } }>(
    '/dashboard/brands/me/discovery-preferences',
    prefs
  );
  return data.data.preferences;
};

type SavePreferencesOptions = {
  /** Skip the success toast: the brand onboarding saves on "Continue", like its other steps. */
  silent?: boolean;
};

export const useSavePreferencesAction = (
  brandId: string,
  { silent = false }: SavePreferencesOptions = {}
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation<BrandDiscoveryPreferences, Error, BrandDiscoveryPreferences>({
    mutationFn: putDiscoveryPreferences,
    onSuccess: (preferences) => {
      queryClient.setQueryData(discoveryPreferencesQueryKey(brandId), preferences);

      if (silent) return;

      toaster.success({
        title: t('companion.prefs.saveSuccess.title'),
        description: t('companion.prefs.saveSuccess.description'),
      });
    },
    onError: () => {
      toaster.error({
        title: t('companion.prefs.saveError.title'),
        description: t('companion.prefs.saveError.description'),
      });
    },
  });
};
